import { Alert, Button, Card, Field, Skeleton, formatDateTime } from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type JSX, useEffect, useRef, useState } from 'react';

import {
  SETTING_CATALOG,
  type ClientSettingDefinition,
  findSettingDefinition,
  parseSettingValue,
  validateSettingValue,
} from '../setting-catalog';
import { AutoCloseSwitch } from './AutoCloseSwitch';
import { mapSystemParametersError } from '../system-parameters-api-errors';
import { getSettingHistory, listSettings, updateSetting } from '../system-parameters.api';

const CATEGORY_LABELS: Record<string, string> = {
  order: 'Pedidos',
  payment: 'Pagos',
};

type FormValues = Record<string, string>;

interface SettingsFormProps {
  canManage: boolean;
  canViewHistory: boolean;
}

function initialValuesFrom(items: { key: string; value: unknown }[]): FormValues {
  const values: FormValues = {};

  for (const definition of SETTING_CATALOG) {
    const item = items.find((candidate) => candidate.key === definition.key);
    values[definition.key] = item === undefined ? '' : String(item.value);
  }

  return values;
}

interface SaveOutcome {
  key: string;
  ok: boolean;
  value: string | undefined;
  error: unknown;
}

/**
 * `settings-hub/spec.md`, "Parámetros del Sistema distingue vista de
 * edición" and `system-settings/spec.md`. One `PATCH` per modified key
 * (design.md §9.6), never a batch endpoint.
 */
export function SettingsForm({ canManage, canViewHistory }: SettingsFormProps): JSX.Element {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['settings'], queryFn: listSettings });

  const [values, setValues] = useState<FormValues>({});
  const [baseline, setBaseline] = useState<FormValues>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const loaded = useRef(false);

  useEffect(() => {
    if (query.data !== undefined && !loaded.current) {
      const initial = initialValuesFrom(query.data);
      setValues(initial);
      setBaseline(initial);
      loaded.current = true;
    }
  }, [query.data]);

  const dirtyKeys = SETTING_CATALOG.filter(
    (definition) => values[definition.key] !== baseline[definition.key],
  ).map((definition) => definition.key);

  const clientErrors: Record<string, string> = {};
  for (const key of dirtyKeys) {
    const definition = findSettingDefinition(key);
    if (definition === undefined) {
      continue;
    }
    const error = validateSettingValue(definition, values[key] ?? '');
    if (error !== undefined) {
      clientErrors[key] = error;
    }
  }

  const saveMutation = useMutation({
    mutationFn: async (): Promise<SaveOutcome[]> => {
      const validKeys = dirtyKeys.filter((key) => clientErrors[key] === undefined);

      return Promise.all(
        validKeys.map(async (key): Promise<SaveOutcome> => {
          const definition = findSettingDefinition(key) as ClientSettingDefinition;

          try {
            await updateSetting(key, parseSettingValue(definition, values[key] ?? ''));
            return { key, ok: true, value: values[key], error: undefined };
          } catch (error) {
            return { key, ok: false, value: undefined, error };
          }
        }),
      );
    },
    onSuccess: (outcomes) => {
      setBaseline((current) => {
        const next = { ...current };
        for (const outcome of outcomes) {
          if (outcome.ok) {
            next[outcome.key] = outcome.value as string;
          }
        }
        return next;
      });

      setServerErrors((current) => {
        const next = { ...current };
        for (const outcome of outcomes) {
          if (outcome.ok) {
            delete next[outcome.key];
          } else {
            const definition = findSettingDefinition(outcome.key) as ClientSettingDefinition;
            next[outcome.key] = mapSystemParametersError(outcome.error, definition);
          }
        }
        return next;
      });

      void queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  const canSave =
    canManage &&
    dirtyKeys.length > 0 &&
    Object.keys(clientErrors).length === 0 &&
    !saveMutation.isPending;

  function handleChange(key: string, next: string): void {
    setValues((current) => ({ ...current, [key]: next }));
    setServerErrors((current) => {
      if (!(key in current)) {
        return current;
      }
      const copy = { ...current };
      delete copy[key];
      return copy;
    });
  }

  const groupedCategories = [...new Set(SETTING_CATALOG.map((definition) => definition.category))];

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSave) {
          saveMutation.mutate();
        }
      }}
    >
      {query.isError ? (
        <Alert tone="danger">No pudimos cargar los parámetros del sistema.</Alert>
      ) : null}

      {query.isPending ? <Skeleton className="h-40 w-full" /> : null}

      {query.data !== undefined
        ? groupedCategories.map((category) => (
            <Card key={category}>
              <div className="flex flex-col gap-4">
                <h2 className="text-sm font-semibold text-slate-900">
                  {CATEGORY_LABELS[category] ?? category}
                </h2>

                {SETTING_CATALOG.filter((definition) => definition.category === category).map(
                  (definition) => (
                    <div key={definition.key} className="flex flex-col gap-2">
                      <Field
                        label={definition.label}
                        name={definition.key}
                        type={definition.inputType === 'number' ? 'number' : 'text'}
                        {...(definition.inputType === 'number' ? { min: 0 } : {})}
                        value={values[definition.key] ?? ''}
                        disabled={!canManage}
                        {...(() => {
                          const message =
                            clientErrors[definition.key] ?? serverErrors[definition.key];
                          return message === undefined
                            ? { hint: definition.helpText }
                            : { error: message };
                        })()}
                        onChange={(event) => handleChange(definition.key, event.target.value)}
                      />

                      {definition.warning !== undefined ? (
                        <Alert tone="warning">{definition.warning}</Alert>
                      ) : null}

                      {(() => {
                        const typed = values[definition.key] ?? '';
                        const preview =
                          definition.preview === undefined || clientErrors[definition.key] !== undefined
                            ? undefined
                            : definition.preview(typed);

                        return preview === undefined ? null : (
                          <p className="text-xs text-slate-600">
                            {`Vista previa del próximo número: ${preview}`}
                          </p>
                        );
                      })()}

                      {canViewHistory ? (
                        <SettingHistorySection settingKey={definition.key} />
                      ) : null}
                    </div>
                  ),
                )}

                {category === 'order' ? (
                  <AutoCloseSwitch settings={query.data} canManage={canManage} />
                ) : null}
              </div>
            </Card>
          ))
        : null}

      {canManage ? (
        <div>
          <Button type="submit" loading={saveMutation.isPending} disabled={!canSave}>
            Guardar
          </Button>
        </div>
      ) : null}
    </form>
  );
}

function SettingHistorySection({ settingKey }: { settingKey: string }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const historyQuery = useQuery({
    queryKey: ['setting-history', settingKey],
    queryFn: () => getSettingHistory(settingKey),
    enabled: false,
  });

  return (
    <div className="flex flex-col gap-1">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => {
          const willExpand = !expanded;
          setExpanded(willExpand);
          if (willExpand) {
            void historyQuery.refetch();
          }
        }}
      >
        Ver historial
      </Button>

      {expanded ? (
        historyQuery.isFetching ? (
          <Skeleton className="h-4 w-48" />
        ) : historyQuery.data !== undefined && historyQuery.data[0] !== undefined ? (
          <p className="text-xs text-slate-500">
            {`Último cambio: ${formatDateTime(historyQuery.data[0].createdAt)}`}
            {historyQuery.data[0].userName !== null ? ` por ${historyQuery.data[0].userName}` : ''}
          </p>
        ) : (
          <p className="text-xs text-slate-500">Sin cambios registrados.</p>
        )
      ) : null}
    </div>
  );
}
